import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { commandCenterApi } from '../services/api'
import {
  Alert,
  Button,
  DataView,
  Dialog,
  Field,
  PageHeader,
  PageLayout,
  StatusBadge,
  Textarea,
} from './common'
import { ApprovalDecisionPanel } from './command-center/approvals/ApprovalDecisionPanel'
import { ApprovalQueue } from './command-center/approvals/ApprovalQueue'
import styles from './CommandCenterApprovals.module.css'

const PENDING_APPROVALS_QUERY_KEY = ['cc-post-groups', 'pending_approval']
const EMPTY_GROUPS = []

function mutationError(error, fallback) {
  return error.response?.data?.error || error.message || fallback
}

function postGroupQuery(id) {
  return { queryKey: ['cc-post-group', id], queryFn: () => commandCenterApi.getPostGroup(id) }
}

// The group the queue moves to once a decision takes id out of it: the one
// that takes its place, else the new last one.
function groupAfterDecision(groups, id) {
  const index = groups.findIndex((group) => group.group_id === id)
  const remaining = groups.filter((group) => group.group_id !== id)
  return remaining[Math.min(Math.max(index, 0), remaining.length - 1)] || null
}

function CommandCenterApprovals() {
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState(null)
  const [mobileReviewOpen, setMobileReviewOpen] = useState(false)
  const [showReject, setShowReject] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [actionError, setActionError] = useState(null)
  const selectedQueueButtonRef = useRef(null)
  const decisionStartRef = useRef(null)
  const pageStartRef = useRef(null)
  const pendingFocusTargetRef = useRef(null)

  const approvalsQuery = useQuery({
    queryKey: PENDING_APPROVALS_QUERY_KEY,
    queryFn: () => commandCenterApi.listPostGroups({ approval_status: 'pending_approval', per_page: 50 }),
  })
  const groups = approvalsQuery.data?.post_groups || EMPTY_GROUPS
  const total = approvalsQuery.data?.total ?? groups.length
  const selectedGroup = groups.find((group) => group.group_id === selectedId) || null

  useEffect(() => {
    if (!groups.length) {
      if (selectedId) pendingFocusTargetRef.current = 'page'
      setSelectedId(null)
      setMobileReviewOpen(false)
      setShowReject(false)
      setRejectReason('')
      setActionError(null)
      return
    }
    if (!groups.some((group) => group.group_id === selectedId)) {
      if (selectedId) pendingFocusTargetRef.current = 'decision'
      setShowReject(false)
      setRejectReason('')
      setActionError(null)
      setSelectedId(groups[0].group_id)
    }
  }, [groups, selectedId])

  useLayoutEffect(() => {
    const pendingTarget = pendingFocusTargetRef.current
    if (!pendingTarget) return
    const target = pendingTarget === 'decision'
      ? decisionStartRef
      : pendingTarget === 'queue' ? selectedQueueButtonRef : pageStartRef
    target.current?.focus()
    pendingFocusTargetRef.current = null
  })

  const detailQuery = useQuery({
    ...postGroupQuery(selectedId),
    enabled: Boolean(selectedId),
  })

  // A decision's next group is read while the decision is sent: the panel
  // shows its spinner on any fetch, so a read after selection would show it.
  const prefetchGroupAfter = (id) => {
    const nextGroup = groupAfterDecision(groups, id)
    if (nextGroup) queryClient.prefetchQuery(postGroupQuery(nextGroup.group_id))
  }

  const completeDecision = (id, message) => {
    const nextGroup = groupAfterDecision(groups, id)

    queryClient.setQueryData(PENDING_APPROVALS_QUERY_KEY, (current) => {
      if (!current) return current
      const currentGroups = current.post_groups || []
      const removed = currentGroups.some((group) => group.group_id === id)
      return {
        ...current,
        post_groups: currentGroups.filter((group) => group.group_id !== id),
        total: removed ? Math.max(0, (current.total ?? currentGroups.length) - 1) : current.total,
      }
    })
    queryClient.removeQueries({ queryKey: ['cc-post-group', id], exact: true })
    pendingFocusTargetRef.current = nextGroup ? 'decision' : 'page'
    setSelectedId(nextGroup?.group_id || null)
    setMobileReviewOpen(Boolean(nextGroup))
    setActionError(null)
    toast.success(message)
    queryClient.invalidateQueries({ queryKey: ['cc-post-groups'] })
  }

  const approveMutation = useMutation({
    mutationFn: ({ id, revision }) => commandCenterApi.approvePostGroup(id, revision),
    onMutate: ({ id }) => {
      setActionError(null)
      prefetchGroupAfter(id)
    },
    onSuccess: (_data, variables) => completeDecision(variables.id, 'Distributed post approved'),
    onError: (error) => {
      const message = mutationError(error, 'Failed to approve')
      setActionError(message)
      toast.error(message)
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, revision, reason }) => commandCenterApi.rejectPostGroup(id, revision, reason),
    onMutate: ({ id }) => {
      setActionError(null)
      prefetchGroupAfter(id)
    },
    onSuccess: (_data, variables) => {
      setShowReject(false)
      setRejectReason('')
      completeDecision(variables.id, 'Distributed post rejected')
    },
    onError: (error) => {
      const message = mutationError(error, 'Failed to reject')
      setActionError(message)
      toast.error(message)
    },
  })

  const deciding = approveMutation.isPending || rejectMutation.isPending
  const closeReject = () => {
    if (rejectMutation.isPending) return
    setShowReject(false)
    setRejectReason('')
    setActionError(null)
  }

  return (
    <PageLayout size="full">
      <PageHeader
        title={<span ref={pageStartRef} tabIndex={-1}>Approvals</span>}
        status={<StatusBadge status="warning">{total} pending</StatusBadge>}
        subtitle="Review the organizations, content, market state, and publish consequence before deciding."
      />

      <DataView
        items={groups}
        loading={approvalsQuery.isLoading}
        error={approvalsQuery.error}
        emptyTitle="No pending approvals"
        emptyDescription="Every submitted distributed post has been reviewed."
        onRetry={() => approvalsQuery.refetch()}
      >
        <div className={`${styles.workspace} ${mobileReviewOpen ? styles.mobileReviewOpen : ''}`.trim()}>
          <div className={styles.queuePane}>
            <ApprovalQueue
              groups={groups}
              total={total}
              selectedId={selectedId}
              disabled={deciding}
              selectedButtonRef={selectedQueueButtonRef}
              onSelect={(id) => {
                if (window.innerWidth <= 768) pendingFocusTargetRef.current = 'decision'
                setSelectedId(id)
                setActionError(null)
                setMobileReviewOpen(true)
              }}
            />
          </div>
          <div className={styles.decisionPane}>
            <ApprovalDecisionPanel
              key={selectedGroup?.group_id || 'no-selection'}
              group={selectedGroup}
              detail={detailQuery.data}
              loading={detailQuery.isLoading || detailQuery.isFetching}
              error={detailQuery.error}
              actionError={actionError}
              deciding={deciding}
              onRetry={() => detailQuery.refetch()}
              onApprove={() => selectedId && detailQuery.data?.revision && approveMutation.mutate({ id: selectedId, revision: detailQuery.data.revision })}
              onReject={() => {
                setActionError(null)
                setShowReject(true)
              }}
              onBack={() => {
                pendingFocusTargetRef.current = 'queue'
                setMobileReviewOpen(false)
                setActionError(null)
              }}
              startFocusRef={decisionStartRef}
            />
          </div>
        </div>
      </DataView>

      <Dialog
        open={showReject && Boolean(selectedGroup)}
        title="Reject distributed post"
        description={selectedGroup
          ? `Rejecting ${selectedGroup.document?.shared?.label || 'this distributed post'} keeps all ${(selectedGroup.destinations || []).length} member posts from publishing.`
          : undefined}
        dismissable={!rejectMutation.isPending}
        onClose={closeReject}
        footer={(
          <>
            <Button variant="secondary" disabled={rejectMutation.isPending} onClick={closeReject}>Cancel</Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              onClick={() => selectedId && detailQuery.data?.revision && rejectMutation.mutate({ id: selectedId, revision: detailQuery.data.revision, reason: rejectReason })}
            >
              Reject distributed post
            </Button>
          </>
        )}
      >
        {actionError ? <Alert variant="error">{actionError}</Alert> : null}
        <Field label="Reason" optional help="The reason is recorded with the rejected group.">
          <Textarea value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} rows={3} />
        </Field>
      </Dialog>
    </PageLayout>
  )
}

export default CommandCenterApprovals
